import { Toast } from '@easyvaas/js-components';
import React, { useEffect, useMemo, useRef } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import SmallText from 'src/components/SmallText';
import { classNames } from 'src/utils/classNames';
import {
  imagesV3Data,
  resetAbility,
  resetAttribute,
  resetDebuff
} from '../../services';
import {
  abilityItemSelector,
  imageItemSelector,
  imagePopupVisibleSelector,
  typedMaterialItemSelector
} from '../../store/selector';
import { imagePopupState } from '../../store/state';
import ImageOpreationPopoup from '../ImageOpreationPopup';
import styles from './index.module.scss';
import DefaultAbilityLogo from '../../images/default-ability@3x.png';
import { useClosePopup } from '../../hooks/useClosePopup';
import { useCheckMaterialEnough } from '../../hooks/useCheckMaterialEnough';

interface MaterialPanelProps {
  image: ImageV3State.Image;
  material: ImageV3API.Material;
}
const MaterialPanel: React.FC<MaterialPanelProps> = ({ material, image }) => {
  const title = useMemo(() => {
    switch (material.type) {
      case 2:
        return '重置普通天赋';
      case 3:
        return '重置高级天赋';
      case 5:
        return '重置属性';
      case 7:
        return '解除异常';
    }
  }, [material.type]);
  const desc = useMemo(() => {
    switch (material.type) {
      case 2:
        return '重置后天赋将替换为随机任意天赋';
      case 3:
        return '重置后天赋将替换为随机任意天赋';
      case 5:
        return '重置属性必定成功';
      case 7:
        return `使用后解除${image.deBuff?.title}1层`;
    }
  }, [image.deBuff?.title, material.type]);
  return (
    <div className={classNames([styles.block, styles.item])}>
      <div className={styles.info}>
        <div
          className={styles.logo}
          style={{ backgroundImage: `url(${material.logo})` }}
        />
        <div className={styles.content}>
          <SmallText fontSize={22} className={styles.text}>
            {material.title}
          </SmallText>
          <SmallText fontSize={22} className={styles.text}>
            拥有 <span style={{ color: '#FF9200' }}>{material.number}</span>
          </SmallText>
          <SmallText fontSize={22} className={styles.text}>
            作用：{title}
          </SmallText>
        </div>
      </div>
      <SmallText fontSize={22} className={styles.text}>
        您将使用
        <span style={{ color: '#FF9200' }}>{material.title}*1</span> {title}，
        {desc}
      </SmallText>
    </div>
  );
};

interface ImageProps {
  imageId: number;
}
const ImageInfo: React.FC<ImageProps> = ({ imageId }) => {
  const image = useRecoilValue(imageItemSelector(imageId));
  const visible = useRecoilValue(imagePopupVisibleSelector('reset'));
  const prev = useRef(image);

  useEffect(() => {
    if (!visible) return;
    if (image) {
      return () => {
        prev.current = image;
      };
    }
  }, [image, visible]);

  if (!image) return null;

  const getChange = (key: 'attackValue' | 'healthPoint') => {
    if (!prev.current) return null;
    const preValue = prev.current[key];
    const curValue = image[key];
    if (!preValue || !curValue) return null;
    if (preValue > curValue) {
      return <div className={styles.down} />;
    } else if (preValue < curValue) {
      return <div className={styles.up} />;
    } else {
      return null;
    }
  };

  return (
    <div className={styles.info}>
      <img className={styles.logo} src={image.url} alt="" />
      <div className={styles.content}>
        <div className={styles.prop}>
          攻击力：{image.attackValue} {getChange('attackValue')}
        </div>
        <div className={styles.prop}>
          生命值：{image.healthPoint} {getChange('healthPoint')}
        </div>
      </div>
    </div>
  );
};
const ImageDesc: React.FC<ImageProps> = ({ imageId }) => {
  const image = useRecoilValue(imageItemSelector(imageId));
  const attackRange = useMemo(() => {
    switch (image?.level) {
      case 1:
        return '10-99';
      case 2:
        return '50-199';
      case 3:
        return '100-499';
      case 4:
        return '100-1000';
      default:
        return '';
    }
  }, [image?.level]);
  const hpRange = useMemo(() => {
    switch (image?.level) {
      case 1:
        return '1000-9999';
      case 2:
        return '5000-19999';
      case 3:
        return '10000-49999';
      case 4:
        return '10000-99999';
      default:
        return '';
    }
  }, [image?.level]);
  if (!image) return null;
  return (
    <div className={styles.block}>
      <SmallText fontSize={22} className={styles.text} inline>
        攻击力取值范围（{attackRange}）
      </SmallText>
      <SmallText fontSize={22} className={styles.text} inline>
        生命值取值范围（{hpRange}）
      </SmallText>
    </div>
  );
};

interface AbilityProps {
  abilityId?: number;
}
const AbilityInfo: React.FC<AbilityProps> = ({ abilityId }) => {
  const item = useRecoilValue(abilityItemSelector(abilityId || 0));
  const visible = useRecoilValue(imagePopupVisibleSelector('reset'));
  const prev = useRef(item);
  useEffect(() => {
    if (!visible) return;
    if (item) {
      return () => {
        prev.current = item;
      };
    }
  }, [item, visible]);
  if (!item) return null;

  const getChange = (key: 'val') => {
    if (!prev.current) return null;
    if (prev.current.isHigh !== item.isHigh) return;
    const preValue = prev.current[key];
    const curValue = item[key];
    if (!preValue || !curValue) return null;
    if (preValue > curValue) {
      return <div className={styles.down} />;
    } else if (preValue < curValue) {
      return <div className={styles.up} />;
    } else {
      return null;
    }
  };
  return (
    <div className={styles.info}>
      <img
        className={styles.logo}
        src={item?.logo || DefaultAbilityLogo}
        alt=""
      />
      <div className={styles.content}>
        <div className={styles.name}>
          {item?.title || '天赋未激活'}{' '}
          {prev.current?.talentId !== item.talentId &&
            prev.current?.isHigh === item.isHigh && (
              <span className={styles.accent}>（新天赋）</span>
            )}
        </div>
        {(item?.val || 0) > 0 && (
          <div>
            战斗系数 {item.val} {getChange('val')}
          </div>
        )}
      </div>
    </div>
  );
};
const AbilityDesc: React.FC<AbilityProps> = ({ abilityId }) => {
  const item = useRecoilValue(abilityItemSelector(abilityId || 0));
  if (!item) return null;
  return (
    <div className={styles.block}>
      <SmallText fontSize={22} className={styles.text}>
        {item.desc}
      </SmallText>
    </div>
  );
};

const ResetPopup = () => {
  const { target, type } = useRecoilValue(imagePopupState('reset'));
  const image = useRecoilValue(imageItemSelector(target));
  const title = useMemo(() => {
    switch (type) {
      case 1:
        return '重置普通天赋';
      case 2:
        return '重置高级天赋';
      case 3:
        return '重置属性';
      case 4:
        return '解除异常';
      default:
        return '';
    }
  }, [type]);
  const materialType = useMemo(() => {
    switch (type) {
      case 1:
        return 2;
      case 2:
        return 3;
      case 3:
        return 5;
      case 4:
        return 7;
      default:
        return 0;
    }
  }, [type]);

  const material = useRecoilValue(typedMaterialItemSelector(materialType));
  const close = useClosePopup();
  const checkMaterial = useCheckMaterialEnough();
  const onClick = useRecoilCallback(({ snapshot }) => async () => {
    if (!image) return;
    if (material && !checkMaterial(material, 1)) return;
    const popup = snapshot.getLoadable(imagePopupState('abilities'));
    if (popup.state !== 'hasValue') return;
    try {
      if (type === 4) {
        Toast.loading('净化中');
      } else {
        Toast.loading('重置中');
      }
      if (type === 1) {
        await resetAbility({ imgId: image.id, isHigh: false });
      } else if (type === 2) {
        await resetAbility({ imgId: image.id, isHigh: true });
      } else if (type === 3) {
        await resetAttribute({ imgId: image.id });
      } else if (type === 4) {
        await resetDebuff({ imgId: image.id });
      }
      await imagesV3Data.refresh({});
      if (type === 4) {
        Toast.show('净化成功');
        if (image.deBuff?.level === 1) {
          close('reset');
        }
      } else {
        Toast.show('重置成功');
      }
    } catch ({ message }) {
      Toast(message);
    }
  });
  if (!image) return null;
  return (
    <ImageOpreationPopoup
      type="reset"
      title={title}
      confirmText={type === 4 ? '净化' : '重置'}
      className={styles.container}
      onClick={onClick}
    >
      {type < 3 && (
        <>
          <AbilityInfo
            abilityId={type === 2 ? image.highTalent : image.talent}
          />
          <AbilityDesc
            abilityId={type === 2 ? image.highTalent : image.talent}
          />
        </>
      )}
      {type === 3 && (
        <>
          <ImageInfo imageId={image.id} />
          <ImageDesc imageId={image.id} />
        </>
      )}
      {type === 4 && image.deBuff && (
        <div className={styles.info}>
          <img className={styles.logo} src={image.deBuff?.logo} alt="" />
          <div className={styles.content}>
            <div className={styles.prop}>
              {image.deBuff.title}（{image.deBuff.level}层）
            </div>
            <div className={styles.prop}>{image.deBuff.desc}</div>
          </div>
        </div>
      )}

      {material && <MaterialPanel material={material} image={image} />}
    </ImageOpreationPopoup>
  );
};

export default React.memo(ResetPopup);
